import React, { useEffect, useRef, useState } from "react"

interface StatItem {
  label: string
  value: number
  suffix?: string
  prefix?: string
  decimals?: number
}

const STATS: StatItem[] = [
  { label: "Years experience", value: 5, suffix: "+" },
  { label: "Projects shipped", value: 40, suffix: "+" },
  { label: "Stack proficiencies", value: 25, suffix: "+" },
  { label: "Avg uptime", value: 99.9, suffix: "%", decimals: 1 },
]

const TONES = ["amber", "mint", "violet", "mint"] as const
type Tone = (typeof TONES)[number]

const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3)

const useCountUp = (
  target: number,
  duration: number,
  decimals: number
): string => {
  const [val, setVal] = useState<number>(0)
  const startRef = useRef<number | null>(null)

  useEffect(() => {
    let raf = 0
    const tick = (ts: number) => {
      if (startRef.current === null) startRef.current = ts
      const elapsed = ts - startRef.current
      const t = Math.min(elapsed / duration, 1)
      setVal(target * easeOut(t))
      if (t < 1) raf = requestAnimationFrame(tick)
      else setVal(target)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])

  return decimals > 0 ? val.toFixed(decimals) : String(Math.floor(val))
}

const StatTile: React.FC<{ stat: StatItem; index: number; tone: Tone }> = ({
  stat,
  index,
  tone,
}) => {
  const display = useCountUp(stat.value, 1300 + index * 120, stat.decimals || 0)
  return (
    <div
      className={`stat-tile stat-tone-${tone}`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="tile-content">
        <div className="tile-head">
          <span className="stat-dot" aria-hidden="true" />
          <span className="stat-label">{stat.label}</span>
        </div>
        <div className="stat-value">
          {stat.prefix}
          <span className="stat-num">{display}</span>
          <span className="stat-suffix">{stat.suffix}</span>
        </div>
      </div>
      <div className="tile-corner tile-corner-tl" />
      <div className="tile-corner tile-corner-tr" />
      <div className="tile-corner tile-corner-bl" />
      <div className="tile-corner tile-corner-br" />
    </div>
  )
}

const StatsPanel: React.FC = () => {
  return (
    <section className="stats-panel" aria-label="Engineering metrics">
      {STATS.map((s, i) => (
        <StatTile key={s.label} stat={s} index={i} tone={TONES[i]} />
      ))}
    </section>
  )
}

export default StatsPanel
